import Axios from 'axios';
import { Availability } from 'interfaces/common';
import { logger } from 'utils';
import { Api, MAX_RETRIES } from 'utils/constants';
import { getApiDomain } from 'utils/url';

export enum VoucherTypes {
  'metro' = 'metro',
  'regional' = 'regional'
}

const isOverride = () => (process.env.REACT_APP_KILLSWITCH || '').trim().toUpperCase() === 'TRUE';

export async function checkAvailability(
  voucherType: VoucherTypes | undefined,
  retryCount = 1
): Promise<{ status: Availability; startTime?: string }> {
  if (isOverride()) {
    return {
      status: 'TOO_LATE'
    };
  }

  try {
    let success: boolean;
    // Add _=<timestamp> to prevent the check from being cached
    if (voucherType) {
      const result = await Axios.get(`${Api.CheckAvailability}?_${Date.now()}`, {
        baseURL: getApiDomain(voucherType)
      });
      success = result.status >= 200 && result.status < 300;
    } else {
      const results = await Promise.all(
        Object.values(VoucherTypes).map(async type => {
          const result = await Axios.get(`${Api.CheckAvailability}?_${Date.now()}`, {
            baseURL: getApiDomain(type)
          });
          return result.status >= 200 && result.status < 300;
        })
      );
      success = results.some(result => result);
    }

    if (!success && retryCount < MAX_RETRIES) {
      return await checkAvailability(voucherType, retryCount + 1);
    }
    // retry exceeded, or was failure
    return {
      status: 'SUCCESS'
    };
  } catch (e: any) {
    if (e.response && typeof e.response.status === 'number') {
      if (e.response.status === 412) {
        return {
          status: 'TOO_EARLY',
          startTime: e.response.data?.message
        };
      } else if (e.response.status === 422) {
        return {
          status: 'TOO_LATE'
        };
      } else if (retryCount < MAX_RETRIES) {
        return await checkAvailability(voucherType, retryCount + 1);
      }
      logger.error('Check Availability Error', e);
      return {
        status: 'TECHNICAL_ERROR'
      };
    } else if (retryCount < MAX_RETRIES) {
      return await checkAvailability(voucherType, retryCount + 1);
    }

    logger.error('Check Availability Error', e);
    return { status: 'TECHNICAL_ERROR' };
  }
}

export async function submitForm<T extends Record<string, any>>(
  value: T,
  voucherType: VoucherTypes,
  retryCount = 1
): Promise<{ status: Availability; transactionId?: string }> {
  if (isOverride()) {
    return {
      status: 'TOO_LATE'
    };
  }
  try {
    const result = await Axios.post<{ message: string; id: string }>(Api.SubmitForm, value, {
      baseURL: getApiDomain(voucherType)
    });
    const success = result.status >= 200 && result.status < 300;

    if (!success && retryCount < MAX_RETRIES) {
      return await submitForm(value, voucherType, retryCount + 1);
    }
    logger.info(`${voucherType} voucher form request successfully submitted.`);
    return {
      status: 'SUCCESS',
      transactionId: result.data.id
    };
  } catch (e: any) {
    if (e.response && typeof e.response.status === 'number') {
      if (e.response.status === 412) {
        logger.error('Transaction rejected - submitted before scheme opened', {
          voucherType,
          transactionID: value.transactionId
        });
        return { status: 'TOO_EARLY' };
      } else if (e.response.status === 422) {
        logger.error('Transaction rejected - quota fulfilled', {
          voucherType,
          transactionID: value.transactionId
        });
        return { status: 'TOO_LATE' };
      } else if (retryCount < MAX_RETRIES) {
        return await submitForm(value, voucherType, retryCount + 1);
      }
      // retry exceeded, or was failure
      logger.error('Submission error after retrying 3x', {
        voucherType,
        transactionID: value.transactionId,
        error: e
      });
      return { status: 'TECHNICAL_ERROR' };
    } else if (retryCount < MAX_RETRIES) {
      return await submitForm(value, voucherType, retryCount + 1);
    }

    logger.error('Submit Form Error', e);
    return { status: 'TECHNICAL_ERROR' };
  }
}
